import React from "react";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import { Form, Input, Select, Radio, Card, Alert, Space, Button, Typography } from "antd";
import { ITaskList, IAdvert } from "../../interfaces";
import { MailOutlined, HomeOutlined, TeamOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

export const TaskListEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<ITaskList>();
  const taskListData = queryResult?.data?.data;

  // Adverts für Auswahl laden
  const { selectProps: advertSelectProps } = useSelect<IAdvert>({
    resource: "senders",
    optionLabel: "title",
    optionValue: "id",
    pagination: {
      pageSize: 100,
    },
  });

  return (
    <Edit saveButtonProps={saveButtonProps} title="Aufgabenliste bearbeiten">
      <Form {...formProps} layout="vertical">
        <Card title="Grundinformationen" style={{ marginBottom: 24 }}>
          <Form.Item
            label="Titel"
            name="title"
            rules={[
              {
                required: true,
                message: "Bitte geben Sie einen Titel ein",
              },
            ]}
          >
            <Input 
              placeholder="z.B. Wöchentliche Reinigung Apartment A" 
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Beschreibung"
            name="description"
          >
            <TextArea
              rows={4}
              placeholder="Beschreiben Sie die Aufgabenliste..."
            />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
          >
            <Select>
              <Select.Option value="open">Offen</Select.Option>
              <Select.Option value="in_progress">In Bearbeitung</Select.Option>
              <Select.Option value="completed">Erledigt</Select.Option>
            </Select>
          </Form.Item>
        </Card>

        <Card title="Zuweisung" style={{ marginBottom: 24 }}>
          <Form.Item
            label={
              <span>
                <MailOutlined /> Zugewiesene E-Mail-Adresse
              </span>
            }
            name="assigned_email"
            rules={[
              {
                required: true,
                message: "Bitte geben Sie eine E-Mail-Adresse ein",
              },
              {
                type: "email",
                message: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
              },
            ]}
            extra="Diese Person erhält Zugriff auf die Aufgabenliste über den Mobile-Link"
          >
            <Input 
              placeholder="reinigungskraft@beispiel.de" 
              prefix={<MailOutlined />}
            />
          </Form.Item>

          {taskListData?.access_token && (
            <Alert
              message="Mobile-Link"
              description={
                <div>
                  <strong>Link:</strong>{" "}
                  <code>{`${window.location.origin}/?type=8847379250&token=${taskListData.access_token}`}</code>
                  <br />
                  <small>Diesen Link an die zugewiesene Person senden</small>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </Card>

        <Card title="Verknüpfungen (Optional)" style={{ marginBottom: 24 }}>
          <Form.Item
            label={
              <span>
                <HomeOutlined /> Objekt/Inserat
              </span>
            }
            name="advert"
          >
            <Select
              {...advertSelectProps}
              placeholder="Objekt auswählen (optional)"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Objektnummer"
            name="object"
            extra="Interne Objektnummer (falls vorhanden)"
          >
            <Input type="number" placeholder="z.B. 12345" />
          </Form.Item>

          <Form.Item
            label="Zimmernummer"
            name="room"
            extra="Spezifische Zimmernummer (falls zutreffend)"
          >
            <Input type="number" placeholder="z.B. 101" />
          </Form.Item>
        </Card>

        <Card title="Sichtbarkeit">
          <Form.Item
            name="visibility"
          >
            <Radio.Group>
              <Radio value="public">
                <TeamOutlined /> Öffentlich - Für alle autorisierten Benutzer sichtbar
              </Radio>
              <Radio value="internal">
                Intern - Nur für Eigentümer und zugewiesene Person sichtbar
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Card>

        {taskListData?.statistics && (
          <Card title="Statistiken" style={{ marginBottom: 24 }}>
            <Alert
              message={`${taskListData.statistics.completed} von ${taskListData.statistics.total} Aufgaben erledigt`}
              description={
                <div>
                  <div>Ausstehend: {taskListData.statistics.pending}</div>
                  <div>In Bearbeitung: {taskListData.statistics.inProgress}</div>
                  <div>Erledigungsrate: {taskListData.statistics.completionRate}%</div>
                </div>
              }
              type="info"
              showIcon
            />
          </Card>
        )}
      </Form>
    </Edit>
  );
};